import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, RefreshCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { LaunchMode } from "@/components/admin/LaunchMode";

// Official app page: pré-pub teaser (auto-generated, editable), trades count prompt for mini-sites.
export const NoveltyEditor = ({ app }) => {
  const { t, fmtDate } = useI18n();
  const [n, setN] = useState(null);
  const [text, setText] = useState("");
  const [trades, setTrades] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setN(d); setText(d.teaser); };
  useEffect(() => {
    api.get(`/admin/novelty/apps/${app.id}`).then(({ data }) => apply(data)).catch(() => setN(false));
  }, [app.id]);
  const run = async (fn) => {
    setBusy(true);
    try {
      apply((await fn()).data);
      toast.success(t("novelty.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  if (!n) return null;
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="novelty-editor">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><Megaphone className="h-4 w-4 shrink-0" /> {t("novelty.editorTitle")}</h2>
      <p className="break-words text-xs text-neutral-400" data-testid="novelty-editor-status">
        {n.first_published_at ? t("novelty.publishedOn", { date: fmtDate(n.first_published_at) }) : t("novelty.notPublished")}
        {n.source && ` · ${t(`novelty.source.${n.source}`)}`}
        {n.trades ? ` · ${t("novelty.tradesFound", { n: n.trades, src: n.trades_source === "admin" ? t("novelty.byAdmin") : n.trades_source })}` : ""}
      </p>
      {n.need_trades && (
        <div className="min-w-0 space-y-2 rounded-lg border border-orange-400/50 bg-orange-950/30 p-3" data-testid="novelty-trades-prompt">
          <p className="break-words text-sm text-orange-100">{t("novelty.tradesPrompt")}</p>
          <div className="flex min-w-0 flex-wrap gap-2">
            <Input type="number" min={1} value={trades} onChange={(e) => setTrades(e.target.value)} className="h-9 w-28 bg-black/50" aria-label={t("novelty.tradesPrompt")} data-testid="novelty-trades-input" />
            <button type="button" className="btn-gold btn-sm" disabled={busy || !Number(trades)} onClick={() => run(() => api.put(`/admin/novelty/apps/${app.id}`, { trades: Number(trades) }))} data-testid="novelty-trades-save">{t("novelty.save")}</button>
            <button type="button" className="btn-red btn-sm" disabled={busy} onClick={() => run(() => api.put(`/admin/novelty/apps/${app.id}`, { dismiss_trades: true }))} data-testid="novelty-trades-dismiss">{t("novelty.dismiss")}</button>
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-3" data-testid="novelty-mega-row">
        <label className="flex items-center gap-2 text-sm text-neutral-200">
          <Switch checked={!n.mega_off} disabled={busy} onCheckedChange={(on) => run(() => api.put(`/admin/novelty/apps/${app.id}`, { mega_off: !on }))} data-testid="novelty-mega-switch" />
          {t("megaG.switch")}
        </label>
        <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${n.brand_detected ? "bg-[var(--gold)]/20 text-[var(--gold-bright)]" : "bg-neutral-800 text-neutral-400"}`} data-testid="novelty-brand-status">
          {n.brand_detected ? t("brand.detected", { reason: n.brand_reason || "" }) : t("brand.notDetected")}
        </span>
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={400} rows={3} placeholder={t("novelty.placeholder")} className="bg-black/50" data-testid="novelty-teaser-input" />
      <div className="flex min-w-0 flex-wrap gap-2">
        <button type="button" className="btn-gold btn-sm" disabled={busy || !text.trim() || text === n.teaser} onClick={() => run(() => api.put(`/admin/novelty/apps/${app.id}`, { teaser: text }))} data-testid="novelty-teaser-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("novelty.save")}
        </button>
        {n.first_published_at && <button type="button" className="btn-red btn-sm" disabled={busy} onClick={() => run(() => api.post(`/admin/novelty/apps/${app.id}/regenerate`))} data-testid="novelty-regenerate">
          <RefreshCcw className="h-4 w-4" /> {t("novelty.regenerate")}</button>}
      </div>
    </section>
  );
};

// Admin setting: banner duration (days).
export const NoveltyDaysCard = () => {
  const { t } = useI18n();
  const [days, setDays] = useState("");
  useEffect(() => {
    api.get("/admin/novelty/settings").then(({ data }) => setDays(String(data.days))).catch(() => {});
  }, []);
  const save = () => api.put("/admin/novelty/settings", { days: Number(days) }).then(() => toast.success(t("novelty.daysSaved"))).catch((err) => toast.error(translateError(err, t)));
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="novelty-days-card">
      <label htmlFor="novelty-days" className="block break-words text-sm font-semibold text-white">{t("novelty.daysLabel")}</label>
      <div className="mt-2 flex flex-wrap gap-2">
        <Input id="novelty-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} disabled={!days} placeholder="…" className="h-9 w-24 bg-black/50" data-testid="novelty-days-input" />
        <button type="button" className="btn-gold btn-sm" disabled={!(Number(days) >= 1 && Number(days) <= 365)} onClick={save} data-testid="novelty-days-save">{t("novelty.save")}</button>
      </div>
      <LaunchMode />
    </section>
  );
};
